import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes anywhere below it and shows a recoverable message
 * instead of a blank screen (part of deliverable 3.12 — surface frontend errors
 * to the operator rather than failing silently). API/data errors are handled
 * per-page; this is the last-resort net for unexpected component failures.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Visible in the browser console for diagnosis; no PII is expected here.
    console.error("Unhandled UI error", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div role="alert" className="grid place-items-center h-full p-8">
          <div className="max-w-md text-center space-y-3">
            <h1 className="text-lg font-semibold">Something went wrong</h1>
            <p className="text-sm text-[var(--fg-muted)]">
              The interface hit an unexpected error. Reloading usually fixes it.
            </p>
            <button
              type="button"
              onClick={() => this.setState({ error: null })}
              className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
